// src/components/Designation/DesignationModal.jsx
import React, { useState, useEffect } from 'react';
import { showNotification } from '../../utils/notification';
import { alertNotification } from '../../utils/notification';

/**
 * DesignationModal Component
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {Function} onSave
 * @param {Object} designation (optional - edit mode)
 * @param {boolean} isSaving
 */
const DesignationModal = ({ isOpen, onClose, onSave, designation, isSaving }) => {
  const MAX_ITEMS = 5;

  // Form state
  const [designationName, setDesignationName] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Table data
  const [designations, setDesignations] = useState([]);

  // Populate data when editing (optional support)
  useEffect(() => {
    if (designation) {
      setDesignations([
        {
          name: designation.name || '',
          isActive: designation.isActive ?? true,
        },
      ]);
    } else {
      setDesignations([]);
    }

    setDesignationName('');
    setIsActive(true);
  }, [designation, isOpen]);

  // Add designation to table
  const addRow = () => {
    if (!designationName.trim()) {
      alert('Designation name is required');
      return;
    }

    if (designations.length >= MAX_ITEMS) {
      alert('Maximum 5 designations allowed');
      return;
    }

    // Duplicate check (case-insensitive)
    const exists = designations.some(
      (d) => d.name.toLowerCase() === designationName.trim().toLowerCase()
    );

    if (exists) {
      alertNotification('Designation already exists', 'error');
      return;
    }

    setDesignations((prev) => [
      ...prev,
      { name: designationName.trim(), isActive },
    ]);

    // Clear form
    setDesignationName('');
    setIsActive(true);
  };

  // Remove designation
  const removeRow = (index) => {
    setDesignations((prev) => prev.filter((_, i) => i !== index));
  };

  // Save designations
  const handleSubmit = (e) => {
    e.preventDefault();

    if (designations.length === 0) {
      alert('Please add at least one designation');
      return;
    }

    onSave({ designations });
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="inventory-modal-overlay">
      <div className="inventory-modal-content">
        {/* Header */}
        <header className="inventory-modal-header">
          <h3 className="inventory-modal-title">
            {designation ? 'Edit Designation' : 'Add New Designation'}
          </h3>
          <button
            className="inventory-close-modal-btn"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            ×
          </button>
        </header>

        {/* Body */}
        <div className="inventory-modal-body">
          <form onSubmit={handleSubmit}>
            {/* FORM INPUTS */}
            <div className="inventory-form-grid">
              <label>
                Designation Name
                <span style={{ color: 'red' }}> *</span>
              </label>
              <input
                type="text"
                className="inventory-custom-input"
                value={designationName}
                onChange={(e) => setDesignationName(e.target.value)}
                disabled={isSaving}
                placeholder="e.g., Manager, Developer, Analyst"
              />

              <label>Active</label>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isSaving}
              />
</div>
              <button
                type="button"
                className="inventory-btn inventory-btn-action inventory-btn-edit"
                onClick={addRow}
                disabled={designations.length >= MAX_ITEMS || isSaving}
                style={{ marginLeft: 'auto', display: 'block' }}
              >
               ➕ Add
              </button>
            

            {/* TABLE */}
            <table className="inventory-data-table" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Designation Name</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {designations.length === 0 ? (
                  <tr>
                    <td colSpan="3">No data</td>
                  </tr>
                ) : (
                  designations.map((desig, index) => (
                    <tr key={index}>
                      <td>{desig.name}</td>
                      <td>{desig.isActive ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          disabled={isSaving}
                        >
                          ❌
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </form>
        </div>

        {/* Footer */}
        <footer className="inventory-modal-footer">
          <button
            className="inventory-btn inventory-btn-cancel"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            Cancel
          </button>
          <button
            className="inventory-btn inventory-btn-save"
            onClick={handleSubmit}
            disabled={isSaving}
            type="button"
          >
            {isSaving ? 'Saving...' : 'Save Designation'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DesignationModal;